import React, { type FunctionComponent, useCallback, useState, useMemo } from 'react'
import { SimpleSelect } from '@retailer-platform/shared-components'
import { useFetchDepartmentsForRetailerQuery } from '__codegen__/api'
import { useDashMessage } from 'intl/intl.hooks'

interface Props {
  retailerId: string
  isDisabled?: boolean
  onChange: (selectedDepartmentId: string) => void
  initialValue?: string
}

export const DepartmentsSelect: FunctionComponent<Props> = ({
  retailerId,
  onChange,
  isDisabled,
  initialValue,
}) => {
  const [departmentId, setDepartmentID] = useState<string>(initialValue)

  const { data, loading, error } = useFetchDepartmentsForRetailerQuery({
    variables: { retailerId },
  })

  const defaultLabel = useDashMessage('components.departmentsSelect.defaultLabel')

  const departments = data?.retailer?.departments

  const departmentsOptions = useMemo(() => {
    if (!departments) return []

    const defaultOption = { label: defaultLabel, value: undefined }
    const departmentOptions = departments.map(dep => ({ label: dep.name, value: dep.id }))

    return [defaultOption, ...departmentOptions]
  }, [departments, defaultLabel])

  const handleOnChange = useCallback(
    (selectedDepartmentId?: string) => {
      setDepartmentID(selectedDepartmentId)
      onChange(selectedDepartmentId)
    },
    [onChange]
  )

  return (
    <SimpleSelect
      loading={loading}
      error={error}
      value={departmentId}
      onChange={handleOnChange}
      options={departmentsOptions}
      aria-label="departments-select"
      isDisabled={isDisabled}
    />
  )
}
